import { eachDayOfInterval, getDay, isSameDay, parseISO } from 'date-fns';
import { z } from 'zod';

import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { WorkingDayResponse } from '../types/dto/time-off-working-days.dto';
import { DayPortion } from '../types/time-off-request';
import {
  timeOffRequestApproverSchema,
  timeOffRequestDateRangeSchema,
  timeOffRequestDaySchema,
} from './time-off-request-day-schema';
import {
  TIME_OFF_FIELD_APPROVAL_REQUIRED,
  TIME_OFF_FIELD_APPROVERS,
  TIME_OFF_FIELD_CATEGORY,
  TIME_OFF_FIELD_DATE_RANGE,
  TIME_OFF_FIELD_DAYS,
  TIME_OFF_FIELD_DESCRIPTION,
  TimeOffRequestFormValues,
} from './time-off-request-form.schema';

/**
 * Create-mode validation over the shared {@link TimeOffRequestFormValues} shape.
 * Applied as the form resolver only in create mode; read/approve reuse the same
 * shape without validation.
 */
export const timeOffRequestCreateSchema = z
  .object({
    [TIME_OFF_FIELD_CATEGORY]: z.string().min(1),
    [TIME_OFF_FIELD_DATE_RANGE]: timeOffRequestDateRangeSchema.nullable(),
    [TIME_OFF_FIELD_DAYS]: z.array(timeOffRequestDaySchema),
    [TIME_OFF_FIELD_APPROVERS]: z.array(timeOffRequestApproverSchema),
    [TIME_OFF_FIELD_DESCRIPTION]: z.string(),
    [TIME_OFF_FIELD_APPROVAL_REQUIRED]: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (!values.dateRange?.from || !values.dateRange?.to) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [TIME_OFF_FIELD_DATE_RANGE] });
    }
    if (!values.days.some(day => day.isWorkingDay)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [TIME_OFF_FIELD_DAYS] });
    }
    // Approvers are required only when the type's policy demands approval.
    if (values.approvalRequired && values.approvers.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [TIME_OFF_FIELD_APPROVERS] });
    }
  });

/** Empty defaults for an unfilled create form. */
export const buildCreateDefaults = (): TimeOffRequestFormValues => ({
  timeOffTypeId: '',
  dateRange: null,
  days: [],
  approvers: [],
  description: '',
  approvalRequired: true,
});

/**
 * Seeds the editable day rows from the working-days response. Working days
 * default to a FULL portion; non-working days are flagged so they render as
 * disabled "non-working day" rows and are excluded from the payload.
 */
export const buildDaysFromWorkingDays = (
  workingDays: WorkingDayResponse[],
  currentDays: TimeOffRequestFormValues['days'] = []
): TimeOffRequestFormValues['days'] =>
  workingDays.map(day => {
    const currentDay = currentDays.find(current =>
      isSameDay(parseISO(current.date), parseISO(day.date))
    );

    if (currentDay) {
      return { ...currentDay, isWorkingDay: day.isWorkingDay };
    }

    return {
      date: day.date,
      isWorkingDay: day.isWorkingDay,
      portion: DayPortion.Full,
      dayPartNumber: '',
      minutes: undefined,
    };
  });

/**
 * Client-side fallback for when the working-days endpoint fails: every day in
 * [from, to] with Saturday + Sunday marked non-working. Returns the same
 * `WorkingDayResponse[]` shape the API would have, so create and read/approve
 * can feed it through their normal seeders unchanged.
 */
export const buildWorkingDaysFromDateRangeFallback = (from: Date, to: Date): WorkingDayResponse[] =>
  eachDayOfInterval({ start: from, end: to }).map(date => {
    const weekday = getDay(date); // 0 = Sunday, 6 = Saturday
    return {
      date: normalizeToStartOfDayUTC(date).toISOString(),
      isWorkingDay: weekday !== 0 && weekday !== 6,
    };
  });
